import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Sparkles, ArrowLeft, Loader2, Plus, Trash2, Image as ImageIcon } from "lucide-react";
import { getJobStatus, postForm } from "../lib/api";

const inputStyle = {
  width: "100%",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-strong)",
  background: "var(--surface)",
  color: "var(--text-primary)",
  padding: "10px 14px",
  fontSize: "0.875rem",
  outline: "none",
  transition: "var(--transition-base)",
};

const cardStyle = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-lg)",
  boxShadow: "var(--shadow-sm)",
  padding: "1.25rem",
};

export default function Pipeline2() {
  const nav = useNavigate();
  const pollRef = useRef(null);
  const activeJobIdRef = useRef(null);

  const [room, setRoom] = useState(null);
  const [roomPreview, setRoomPreview] = useState(null);
  const [items, setItems] = useState([{ name: "", file: null, preview: null }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [statusText, setStatusText] = useState(null);
  const [jobId, setJobId] = useState(null);

  useEffect(() => {
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, []);

  const handleRoomChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRoom(file);
    const reader = new FileReader();
    reader.onloadend = () => setRoomPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const addItem = () => { if (items.length < 7) setItems([...items, { name: "", file: null, preview: null }]); };
  const removeItem = (idx) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };

  const updateItem = (idx, patch) => {
    const next = [...items];
    const updated = { ...next[idx], ...patch };
    if (patch.file) {
      const reader = new FileReader();
      reader.onloadend = () => { next[idx] = { ...updated, preview: reader.result }; setItems([...next]); };
      reader.readAsDataURL(patch.file);
      return;
    }
    next[idx] = updated; setItems(next);
  };

  function stopPolling() {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
  }

  async function pollOnce(id) {
    const status = await getJobStatus(id);
    if (activeJobIdRef.current !== id) return;
    setStatusText(status.message ?? `Job status: ${status.status}`);
    if (status.status === "completed") {
      stopPolling(); setBusy(false);
      if (status.result_url) nav(`/interior-design-ai/result?url=${encodeURIComponent(status.result_url)}`);
      else setErr("Job completed but result URL was missing.");
      return;
    }
    if (status.status === "failed") { stopPolling(); setBusy(false); setErr(status.error ?? "Pipeline execution failed"); }
  }

  function startPolling(id) {
    if (!id) { setBusy(false); setErr("Missing job_id from backend response"); return; }
    activeJobIdRef.current = id; stopPolling();
    pollOnce(id).catch((e) => { stopPolling(); setBusy(false); setErr(e.message); });
    pollRef.current = window.setInterval(async () => {
      try { await pollOnce(id); } catch (e) { stopPolling(); setBusy(false); setErr(e.message); }
    }, 3000);
  }

  async function run() {
    if (!room) return;
    const valid = items.filter((x) => x.name.trim() && x.file);
    if (!valid.length) { setErr("Please add at least 1 furniture image and its name."); return; }
    stopPolling(); activeJobIdRef.current = null;
    setErr(null); setBusy(true); setStatusText("Submitting job..."); setJobId(null);
    try {
      const fd = new FormData();
      fd.append("room_image", room);
      fd.append("furniture_names", valid.map((v) => v.name.trim()).join(","));
      valid.forEach((v) => fd.append("furniture_images", v.file));
      const data = await postForm("/run/pipeline2", fd);
      const nextId = data?.job_id ? String(data.job_id) : "";
      if (!nextId) throw new Error("Backend did not return job_id");
      setJobId(nextId); setStatusText(data?.message ?? "Job queued..."); startPolling(nextId);
    } catch (e) { stopPolling(); setErr(e.message ?? "Pipeline execution failed"); setBusy(false); setStatusText(null); }
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-soft)" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-6">

        {/* Back + header */}
        <div className="animate-fade-up">
          <button
            onClick={() => nav("/interior-design-ai/dashboard")}
            className="inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 mb-3 transition-all"
            style={{ color: "var(--text-secondary)", border: "1px solid var(--border-strong)", background: "var(--surface)" }}
          >
            <ArrowLeft className="w-3 h-3" /> Dashboard
          </button>
          <h2 className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}>
            Custom Assets Pipeline
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Upload your room and the specific furniture you want to place
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: room upload + submit */}
          <div className="lg:col-span-1">
            <div style={{ ...cardStyle, position: "sticky", top: "5rem" }} className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}>
                <Upload className="w-4 h-4" style={{ color: "var(--orange)" }} /> Base Room
              </h3>
              <div
                className={`iai-upload-zone relative aspect-square flex flex-col items-center justify-center overflow-hidden ${roomPreview ? "has-file" : ""}`}
                onClick={() => document.getElementById("room-upload-p2")?.click()}
              >
                {roomPreview
                  ? <img src={roomPreview} className="w-full h-full object-cover" style={{ borderRadius: "var(--radius-md)" }} alt="Preview" />
                  : (
                    <div className="text-center space-y-2 p-4">
                      <Upload className="w-6 h-6 mx-auto" style={{ color: "var(--text-muted)" }} />
                      <p className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>Click to upload base room</p>
                    </div>
                  )
                }
                <input id="room-upload-p2" type="file" accept="image/*" className="hidden" onChange={handleRoomChange} />
              </div>

              {jobId && <p className="text-xs" style={{ color: "var(--text-muted)" }}>Job ID: {jobId}</p>}
              {statusText && <div className="iai-status-info">{statusText}</div>}
              {err && <div className="iai-status-error">{err}</div>}

              <button
                onClick={run}
                disabled={!room || busy}
                className="btn-primary-glow w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(135deg, var(--orange), var(--orange-light))" }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy ? "Processing..." : "Generate Result"}
              </button>
            </div>
          </div>

          {/* Right: furniture items */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}>
                <Plus className="w-4 h-4" style={{ color: "var(--orange)" }} /> Furniture Items ({items.length}/7)
              </h3>
              <button
                onClick={addItem}
                disabled={items.length >= 7}
                className="inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 transition-all disabled:opacity-40"
                style={{ color: "var(--orange)", border: "1px solid var(--orange-glow)", background: "var(--orange-pale)" }}
              >
                <Plus className="w-3 h-3" /> Add Item
              </button>
            </div>

            <div className="space-y-3">
              {items.map((it, idx) => (
                <div key={idx} style={cardStyle} className="flex flex-col sm:flex-row gap-4">
                  {/* Thumbnail */}
                  <div
                    className={`iai-upload-zone w-20 h-20 shrink-0 flex items-center justify-center overflow-hidden ${it.preview ? "has-file" : ""}`}
                    style={{ borderRadius: "var(--radius-md)", minWidth: 80 }}
                    onClick={() => document.getElementById(`file-${idx}`)?.click()}
                  >
                    {it.preview
                      ? <img src={it.preview} className="w-full h-full object-cover" style={{ borderRadius: "var(--radius-md)" }} alt="Item" />
                      : <ImageIcon className="w-5 h-5" style={{ color: "var(--text-muted)" }} />
                    }
                    <input id={`file-${idx}`} type="file" accept="image/*" className="hidden" onChange={(e) => updateItem(idx, { file: e.target.files?.[0] ?? null })} />
                  </div>

                  {/* Name + delete */}
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      className="input-premium flex-1"
                      style={inputStyle}
                      value={it.name}
                      onChange={(e) => updateItem(idx, { name: e.target.value })}
                      placeholder="Furniture name (e.g. Modern Sofa)"
                    />
                    <button
                      onClick={() => removeItem(idx)}
                      disabled={items.length <= 1}
                      className="p-2 rounded-lg shrink-0 transition-colors disabled:opacity-30"
                      style={{ color: "var(--text-muted)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "#dc2626")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
